/* eslint-disable camelcase */
import { FieldArray, Form, Formik } from 'formik';
import InputText from '../../../../components/input-text/inputText';
import ModalComponent from '../../../../components/modal/modal';
import ModalFooterComponent from '../../../../components/modal/modalFooterComponent';
import ModalBodyComponent from '../../../../components/modal/modalBodyComponent';
import { useGetAlertTypes, useWealthlaneCustomerApi } from '../../../../hooks';
import _ from 'lodash';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import usePostSupportContacts from '../../../../hooks/post/usePostSupportContacts';
import { Wealthlane_CustomerService_Dtos_SupportContact_SupportContactDto } from '../../../../services/wealthlane-customer-services/models/Wealthlane_CustomerService_Dtos_SupportContact_SupportContactDto';
import { EMAIL_VALIDATION_SCHEMA } from '../../../../utils/validations/validations';
import FormSelect from '../../../../components/dropdown/formSelect';

interface SupportEmailModalProps {
  isModalOpen: boolean;
  closeModal: () => void;
  supportContacts?: Wealthlane_CustomerService_Dtos_SupportContact_SupportContactDto[];
  loading?: boolean;
  id?: string;
  companyId?: string;
}

interface EmailFormValues {
  companyId?: string;
  alertType: string;
  emails: Array<{
    id?: string | null;
    email: string;
  }>;
}

const SupportEmailModal = ({
  isModalOpen,
  closeModal,
  supportContacts,
  loading = false,
  id,
  companyId,
}: SupportEmailModalProps): JSX.Element => {
  const { handleRequest } = useWealthlaneCustomerApi();
  const { data: alertTypes, isLoading: isLoadingAlertTypes } = useGetAlertTypes(handleRequest);
  const { mutate, isLoading } = usePostSupportContacts(handleRequest, closeModal);

  const initialValues: EmailFormValues = {
    companyId: companyId ?? '',
    alertType: alertTypes?.[0]?.value ?? '',
    emails:
      supportContacts && supportContacts.length > 0
        ? supportContacts.map((contact) => ({
            id: contact.id,
            email: contact.email ?? '',
          }))
        : [{ id: null, email: '' }],
  };

  const handleSubmit = (values: EmailFormValues): void => {
    const payload = {
      companyId: companyId ?? null,
      alertType: values.alertType,
      supportContacts: values.emails
        .filter((item) => item.email.trim() !== '')
        .map((item) => ({
          id: item.id,
          email: item.email,
        })),
    };
    mutate(payload);
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={EMAIL_VALIDATION_SCHEMA}
      onSubmit={(values) => {
        handleSubmit(values);
      }}>
      {({ touched, handleChange, setFieldValue, errors, values }) => {
        return (
          <ModalComponent
            show={isModalOpen}
            closeModal={closeModal}
            size='md'
            loading={loading}
            title='Manage Alert Subscriptions'
            valueEdited={_.isEqual(values, initialValues)}
            id={id}>
            <Form id='supportEmailForm'>
              <ModalBodyComponent id='supportEmailModalBody'>
                <div className='grid grid-cols-4'>
                  <FormSelect
                    containerClassName='col-span-3'
                    label='Alert Type'
                    name='alertType'
                    viewName='alertType'
                    id='alertType'
                    onChange={handleChange}
                    errors={errors}
                    touched={touched}
                    isLoading={isLoadingAlertTypes}
                    options={alertTypes}
                    values={values}
                    setFieldValue={setFieldValue}
                    disabled={true}
                    required
                  />
                </div>
                <div className=' pt-0' id='supportEmailFormWrapper'>
                  <FieldArray
                    name='emails'
                    render={(arrayHelpers) => {
                      return (
                        <TransitionGroup className='todo-list' id='supportEmailTransitionGroup'>
                          {values.emails.map((item, index) => (
                            <CSSTransition<undefined>
                              addEndListener={(node: HTMLElement, done: () => void) => {
                                node.addEventListener('transitionend', done, false);
                              }}
                              classNames='fade'
                              key={index}>
                              <div
                                className='grid grid-cols-4 items-center gap-3 mb-4'
                                id={`supportEmailItem.${index}`}>
                                <div className='col-span-3'>
                                  <InputText
                                    type='email'
                                    label={index === 0 ? 'Alert Subscription' : ''}
                                    name={`emails.${index}.email`}
                                    id={`emails.${index}.email`}
                                    placeholder='Enter email address'
                                    onChange={handleChange}
                                    errorValue={(errors?.emails as any)?.[index]?.email}
                                    touchedValue={(touched?.emails as any)?.[index]?.email}
                                    errors={errors}
                                    touched={touched}
                                    required
                                  />
                                </div>
                                <div
                                  className={`flex items-start gap-2  ${
                                    index === 0 ? 'mt-7 mb-4' : 'mt-1 mb-8'
                                  }`}>
                                  {values.emails.length > 1 && (
                                    <div
                                      className='flex items-center cursor-pointer p-1 rounded hover:bg-gray-100'
                                      role='button'
                                      id={`supportEmailDeleteBtn.${index}`}
                                      onClick={() => {
                                        // toast.error(
                                        //   <ToastComponent label='Error' message='Email removed' />
                                        // );
                                        arrayHelpers.remove(index);
                                      }}>
                                      <span
                                        className='material-symbols-outlined text-red-600'
                                        style={{ fontSize: '22px' }}
                                        id={`supportEmailDeleteIcon.${index}`}>
                                        delete
                                      </span>
                                    </div>
                                  )}
                                  {index === values.emails.length - 1 && (
                                    <div
                                      className='flex items-center cursor-pointer p-1 rounded hover:bg-gray-100'
                                      role='button'
                                      id={`supportEmailAddBtn.${index}`}
                                      onClick={() => {
                                        arrayHelpers.push({ id: null, email: '' });
                                      }}>
                                      <span
                                        className='material-symbols-outlined text-green-600'
                                        style={{ fontSize: '22px' }}
                                        id={`supportEmailAddIcon.${index}`}>
                                        add_circle
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </CSSTransition>
                          ))}
                        </TransitionGroup>
                      );
                    }}
                  />
                </div>
              </ModalBodyComponent>
              <ModalFooterComponent
                modalStatus='Edit'
                onCloseModal={() => {
                  closeModal();
                }}
                valueChanged={_.isEqual(values, initialValues)}
                loading={isLoading}
                id='supportEmailModalFooter'
              />
            </Form>
          </ModalComponent>
        );
      }}
    </Formik>
  );
};

export default SupportEmailModal;
